import React, { useState, memo, useEffect } from 'react';
import { Box, styled } from '@mui/material';
import { handlePercentageChange, stripPercentSymbol } from '@utils/percentageInputsUtils';
import { convertDecimalToPercentage, convertPercentageToDecimal } from './utils';
import { TableCellVariant } from './types';

interface PercentageInputCellProps {
  value: number | null;
  isEditable: boolean;
  row: number;
  col: number;
  onChange: (value: number | string | null, row: number, col: number) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>, row: number, col: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  variant: TableCellVariant;
  isSelected?: boolean;
  isCopied?: boolean;
  isPasted?: boolean;
  autofocus?: boolean;
  shouldFormatValue?: boolean;
}

const cellStyles = {
  backgroundColor: 'white',
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 600,
  color: '#1B1C17',
};

// transparent browser selection
const StyledInput = styled('input')({
  width: '100%',
  height: '100%',
  border: 'none',
  outline: 'none',
  textAlign: 'center',
  '::selection': {
    backgroundColor: '#FFFFFF00',
  },
});

const percentageGrayCellStyles = {
  fontSize: '14px',
  fontWeight: 700,
  color: '#6C6C6C',
  backgroundColor: '#F0F0F1',
  textAlign: 'right',
  paddingRight: '8px',
};

const percentageDisabledCellStyles = {
  fontSize: '14px',
  fontWeight: 700,
  color: '#B4B4B4',
  backgroundColor: '#F0F0F1',
  textAlign: 'right',
  paddingRight: '8px',
};

const percentageEditableCellStyles = {
  fontSize: '14px',
  fontWeight: 500,
  color: '#1B1C17',
  backgroundColor: '#FFFFFF',
  textAlign: 'right',
  paddingRight: '8px',
};

const percentageDisabledLightCellStyles = {
  fontSize: '14px',
  fontWeight: 500,
  color: '#6C6C6C',
  backgroundColor: '#FFFFFF',
  textAlign: 'right',
  paddingRight: '8px',
};

const resolveVariantStyles = (variant: TableCellVariant): any => {
  switch (variant) {
    case 'percentage-gray':
      return percentageGrayCellStyles;
    case 'percentage-disabled':
      return percentageDisabledCellStyles;
    case 'percentage-editable':
      return percentageEditableCellStyles;
    case 'percentage-disabled-light':
      return percentageDisabledLightCellStyles;
    default:
      return cellStyles;
  }
};

const PercentageInputCell = memo(
  ({
    value: initialValue,
    isEditable,
    row,
    col,
    shouldFormatValue = true,
    onChange,
    onPaste,
    onFocus,
    onBlur,
    variant,
    isSelected,
    isCopied,
    isPasted,
    autofocus,
  }: PercentageInputCellProps) => {
    const [formattedValue, setFormattedValue] = useState(
      initialValue !== null ? convertDecimalToPercentage(initialValue) : '',
    );

    useEffect(() => {
      if (shouldFormatValue) {
        setFormattedValue(initialValue !== null ? convertDecimalToPercentage(initialValue) : '');
      } else {
        setFormattedValue(initialValue !== null ? initialValue.toString() : '');
      }
    }, [initialValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value || '';

      // Sanitize input: only allow -, %, 0-9, and .
      const sanitizedInput = inputValue.replace(/[^-%.0-9]/g, '');

      // If input contains invalid characters, prevent update
      if (sanitizedInput !== inputValue) {
        e.preventDefault();
        return;
      }

      if (inputValue === '' || inputValue === '%') {
        setFormattedValue('');
        onChange(null, row, col);
        return;
      }

      if (inputValue === '-') {
        setFormattedValue('-');
        return;
      }

      if (shouldFormatValue) {
        const sanitizedValue = stripPercentSymbol(inputValue).toString();
        let truncatedValue = sanitizedValue;
        if (sanitizedValue.includes('.')) {
          const [intPart, decimalPart] = sanitizedValue.split('.');
          truncatedValue = `${intPart}.${decimalPart.slice(0, 2)}`; // take only first 2 digits
        }

        const newActualValue = convertPercentageToDecimal(truncatedValue);
        handlePercentageChange(e, setFormattedValue);
        onChange(newActualValue, row, col);
      } else {
        // When formatting is disabled, handle decimal input directly
        const cleanValue = inputValue.replace(/%/g, ''); // Remove % if present
        let truncatedValue = cleanValue;

        // Handle decimal truncation
        if (cleanValue.includes('.')) {
          const [intPart, decimalPart] = cleanValue.split('.');
          truncatedValue = `${intPart}.${decimalPart.slice(0, 2)}`; // take only first 2 digits
        }

        // Allow '.' and '-.' to be displayed while typing
        if (truncatedValue === '.' || truncatedValue === '-.') {
          setFormattedValue(truncatedValue);
          onChange(null, row, col);
          return;
        }

        const newActualValue =
          truncatedValue === '' || truncatedValue === '-'
            ? null
            : isNaN(parseFloat(truncatedValue))
              ? null
              : parseFloat(truncatedValue);

        setFormattedValue(truncatedValue);
        onChange(newActualValue, row, col);
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (onPaste) {
        onPaste(e, row, col);
      }
    };

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isEditable ? (
          <StyledInput
            style={{
              ...resolveVariantStyles(variant),
              ...(isSelected && { backgroundColor: '#2F736E2F', color: '#1B1C17' }),
              ...(isCopied && {
                outline: '2px dashed #2F736E',
                outlineOffset: '-3px',
                backgroundColor: '#2F736E2F',
              }),
              ...(isPasted && { backgroundColor: '#2F736E2F', color: '#1B1C17' }),
            }}
            type="text"
            value={formattedValue}
            onChange={handleChange}
            onPaste={handlePaste}
            onFocus={onFocus}
            onBlur={onBlur}
            autoFocus={autofocus}
          />
        ) : initialValue !== null ? (
          shouldFormatValue ? (
            convertDecimalToPercentage(initialValue)
          ) : (
            initialValue
          )
        ) : (
          ''
        )}
      </Box>
    );
  },
);

PercentageInputCell.displayName = 'PercentageInputCell';

export default PercentageInputCell;
